import Container from "@/components/Container";
import configuration from "@/configuration";
import i18n from "@/i18n";
import instance from "@/services/axiosConfig";
import { LocaleService, VersionService } from "@/services/global-services";
import { useDataStore, useFilterStore } from "@/stores";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { alpha, AppBar, Box, Divider, Paper, useMediaQuery } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import Footer from "../Footer";
import LoadingView from "../LoadingView";
import Sidebar from "../Sidebar";
import Topbar from "../TopBar";

interface WrapperProps {
    children: React.ReactNode;
}

const Wrapper = ({ children }: WrapperProps) => {
    const [interceptorSetup, setInterceptorSetup] = useState<boolean>(false);
    const interceptorRef = useRef<number | null>(null);

    const [canRender, setCanRender] = useState<boolean>(false);
    const getLocalisedContentCalled = useRef<boolean>(false);

    const [openSidebar, setOpenSidebar] = useState<boolean>(false);

    const activeVersion = useFilterStore(useShallow((state) => state.activeVersion));
    const setActiveVersion = useFilterStore((state) => state.setActiveVersion);

    const versions = useDataStore((state) => state.versions);
    const setVersions = useDataStore((state) => state.setVersions);
    const setDisciplines = useDataStore((state) => state.setDisciplines);
    const setPhases = useDataStore((state) => state.setPhases);
    const setSoftware = useDataStore((state) => state.setSoftware);
    const setUseCases = useDataStore((state) => state.setUseCases);

    const isMd = useMediaQuery((theme) => theme.breakpoints.up("md"), {
        defaultMatches: true,
    });

    const { getAccessTokenSilently } = useAuth0();

    const localeService = useMemo(() => new LocaleService("be", "en"), []);

    useEffect(() => {
        interceptorRef.current = instance.interceptors.request.use(
            async (config) => {
                if (config.url?.startsWith(configuration.assets)) return config;

                const accessToken = await getAccessTokenSilently();
                config.headers["Authorization"] = `Bearer ${accessToken}`;

                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        setInterceptorSetup(true);

        return () => {
            if (!interceptorRef.current) return;
            instance.interceptors.request.eject(interceptorRef.current);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const getVersions = async () => {
            const versions = await VersionService.get();

            setActiveVersion("wip");
            setVersions(versions);
        };

        getVersions();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (versions.length === 0) return;

        getLocalisedContent();
        i18n.changeLanguage("en");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [versions, activeVersion]);

    const getLocalisedContent = async () => {
        if (!activeVersion || getLocalisedContentCalled.current) return;

        getLocalisedContentCalled.current = true;

        const activeVersionName = "wip";

        await Promise.all([
            localeService
                .getSoftware(activeVersionName)
                .then((software) => setSoftware(activeVersion, software))
                .catch(() => setSoftware(activeVersion, [])),
            localeService
                .getDisciplines(activeVersionName)
                .then((disciplines) => setDisciplines(activeVersion, disciplines))
                .catch(() => setDisciplines(activeVersion, [])),
            localeService
                .getPhases(activeVersionName)
                .then((phases) => setPhases(activeVersion, phases))
                .catch(() => setPhases(activeVersion, [])),
            localeService
                .getUseCases(activeVersionName)
                .then((useCases) => setUseCases(activeVersion, useCases))
                .catch(() => setUseCases(activeVersion, [])),
        ]);

        setCanRender(true);
    };

    const handleSidebarOpen = () => setOpenSidebar(true);

    const handleSidebarClose = () => setOpenSidebar(false);

    const open = isMd ? false : openSidebar;

    if (!interceptorSetup)
        return (
            <div className="flex fixed top-0 right-0 bottom-0 left-0 items-center justify-center">
                Authenticating...
            </div>
        );

    if (!canRender) {
        return <LoadingView />;
    }

    return (
        <Paper elevation={0}>
            <Box>
                <AppBar
                    position={"fixed"}
                    sx={(theme) => ({
                        backgroundColor: theme.palette.background.paper,
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    })}
                    elevation={0}
                >
                    <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
                        <Topbar onSidebarOpen={handleSidebarOpen} />
                    </Container>
                </AppBar>

                <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? "permanent" : "temporary"} />

                <main>
                    <Box height={{ xs: 58, sm: 66, md: 71 }} />
                    <Box display="flex" flex="1 1 auto" overflow="hidden" paddingLeft={{ md: "256px" }}>
                        <Box display="flex" flex="1 1 auto" overflow="hidden">
                            <Box flex="1 1 auto" height="100%" overflow="auto">
                                <Box>{children}</Box>
                                <Divider />
                                <Container paddingY={4}>
                                    <Footer />
                                </Container>
                            </Box>
                        </Box>
                    </Box>
                </main>
            </Box>
        </Paper>
    );
};

const AuthenticatedWrapper = withAuthenticationRequired(Wrapper, {
    onRedirecting: () => (
        <div className="flex fixed top-0 right-0 bottom-0 left-0 items-center justify-center">Authenticating...</div>
    ), // You can customize this loading state
});

export default AuthenticatedWrapper;
